import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Modal from "react-modal";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import moment from "moment";
import infoIcon from "../../images/icons/info-icon.svg";
import emailImg from "../../images/user/rey.svg";
import { useUserSuggestions } from "../User/userProfileQueries";
import CalendarIcon from '../ui/CalendarIcon';

const REMINDER_METHODS = [
    { value: 'popup', label: 'Popup Notification' },
    { value: 'email', label: 'Email Notification' },
];

const SUPER_ADMIN = 1;
const ADMIN = 2;
const USER = 3;
const PROJECT_MANAGER = 6;
const TEAM_MANAGER = 7;

const ranges = [
    {
        label: "Today",
        value: [new Date(), new Date()],
    },
    {
        label: "Tomorrow",
        value: [moment().add(1, "days").toDate(), moment().add(1, "days").toDate()],
    },
    {
        label: "Next 7 Days",
        value: [new Date(), moment().add(6, "days").toDate()],
    },
];

const REPEAT_TYPES = [
    { value: "does_not_repeat", label: "Does Not Repeat" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
    { value: "all_day", label: "All Day" },
];

const CreateEventModal = ({
    createEventModalOpen,
    setCreateEventModalOpen,
    isSubmitLoading,
    setIsSubmitLoading,
    isGoogleSyncVisible,
    isMicrosoftSyncVisible,
    googleUsers,
    microsoftUsers,
    setIsMicrosoftTokenInvalidModalOpen,
    eventName,
    setEventName,
    eventNameIsValid,
    setEventNameIsValid,
    repeatType,
    setRepeatType,
    repeatTypeIsValid,
    setRepeatTypeIsValid,
    repeatTypeDropdownOpen,
    setRepeatTypeDropdownOpen,
    eventDate,
    setEventDate,
    eventDateIsValid,
    setEventDateIsValid,
    // dateRangeType,
    // setDateRangeType,
    // setDateRange,
    appSettings,
    isSlotSelected,
    setIsSlotSelected,
    eventStartTime,
    setEventStartTime,
    startTimeIsValid,
    setStartTimeIsValid,
    eventStartTimeIsValid,
    setEventStartTimeIsValid,
    eventEndTime,
    endTimeIsValid,
    setStartTime,
    setEndTime,
    setEndTimeIsValid,
    setEventEndTime,
    eventEndTimeIsValid,
    setEventEndTimeIsValid,
    eventDescription,
    setEventDescription,
    eventLocation,
    setEventLocation,
    eventBannerFile,
    setEventBanner,
    // setDateRangeIsValid,
    endTimeString,
    setEndTimeString,
    startTimeString,
    setStartTimeString,
    user,
    eventOrganizer,
    setEventOrganizer,
    organizerDropdownOpen,
    setOrganizerDropdownOpen,
    userProfiles,
    userProfileLoading,
    eventAttendees,
    setEventAttendees,
    externalAttendees,
    setExternalAttendees,
    isVirtual,
    setIsVirtual,
    eventMeetingLink,
    setEventMeetingLink,
    reminderMethod,
    setReminderMethod,
    reminderMethodDropdownOpen,
    setReminderMethodDropdownOpen,
    reminderMinutes,
    setReminderMinutes,
    reminderMinutesIsValid,
    setReminderMinutesIsValid,
    token,
    setIsMicrosoftTokenChecking,
    checkMicrosoftTokenValidity,
    checkMicrosoftCalendarEnabledStatus,
    refetchEvents,
    refetchSelectedGoogleEvents,
    refetchSelectedMicrosoftEvents,
}) => {
    const [syncWithGoogleCalendar, setSyncWithGoogleCalendar] = useState(false);
    const [syncWithMicrosoftCalendar, setSyncWithMicrosoftCalendar] = useState(false);
    const [selectedGoogleUserId, setSelectedGoogleUserId] = useState(googleUsers.length > 0 ? googleUsers[0]?._id : null);
    const [selectedMicrosoftUserId, setSelectedMicrosoftUserId] = useState(microsoftUsers.length > 0 ? microsoftUsers[0]?._id : null);
    const [eventEndDate, setEventEndDate] = useState(null);
    const [attendeeInput, setAttendeeInput] = useState('');
    const [attendeeSuggestions, setAttendeeSuggestions] = useState([]);
    
    // Ensure eventAttendees and externalAttendees are always arrays
    const safeEventAttendees = Array.isArray(eventAttendees) ? eventAttendees : [];
    const safeExternalAttendees = Array.isArray(externalAttendees) ? externalAttendees : [];
    const [eventEndDateIsValid, setEventEndDateIsValid] = useState(false);
    const eventNameRef = useRef(null);
    const repeatTypeRef = useRef(null);
    const eventDateRef = useRef(null);
    const eventEndDateRef = useRef(null);
    const eventStartTimeRef = useRef(null);
    const eventEndTimeRef = useRef(null);
    const organizerRef = useRef(null);
    const eventMeetingLinkRef = useRef(null);
    const reminderMethodRef = useRef(null);
    const eventOrganizerRef = useRef("");
    const prevUserSuggestionsRef = useRef(null);
    const prevUserProfilesRef = useRef(null);
    const prevEventOrganizerRef = useRef(null);
    const prevGoogleUsersRef = useRef(null);
    const prevMicrosoftUsersRef = useRef(null);
    const prevEventAttendeesRef = useRef(null);
    const prevExternalAttendeesRef = useRef(null);
    const prevAttendeeInputRef = useRef(null);
    const dummyImage = "/Uploads/lowProfileImage/user_default.png";

    // Fetch user suggestions for attendee selection
    const { data: userSuggestions = [], isLoading: userSuggestionsLoading } = useUserSuggestions(token);

    useEffect(() => {
        if (eventOrganizer) {
            eventOrganizerRef.current = eventOrganizer;
        }
    }, [eventOrganizer]);

    // Clear organizer when sync is enabled
    useEffect(() => {
        if (syncWithGoogleCalendar || syncWithMicrosoftCalendar) {
            setEventOrganizer("");
        } else {
            // Restore the previous organizer if syncing is turned off
            if (!eventOrganizer && eventOrganizerRef.current) {
                setEventOrganizer(eventOrganizerRef.current);
            }
        }
    }, [syncWithGoogleCalendar, syncWithMicrosoftCalendar]);

    useEffect(() => {
        // Check if values have actually changed
        const googleUsersString = JSON.stringify(googleUsers);
        const microsoftUsersString = JSON.stringify(microsoftUsers);
        
        const googleUsersChanged = prevGoogleUsersRef.current !== googleUsersString;
        const microsoftUsersChanged = prevMicrosoftUsersRef.current !== microsoftUsersString;
        
        // Only proceed if something actually changed
        if (!googleUsersChanged && !microsoftUsersChanged && prevGoogleUsersRef.current !== null) {
            return;
        }
        
        // Update refs
        prevGoogleUsersRef.current = googleUsersString;
        prevMicrosoftUsersRef.current = microsoftUsersString;
        
        // Google user selection
        if (syncWithGoogleCalendar && googleUsers.length > 0) {
            const validGoogleUsers = googleUsers.filter(user => user.isValid);
            setSelectedGoogleUserId(validGoogleUsers.length > 0 ? validGoogleUsers[0]?._id : null);
        } else {
            setSelectedGoogleUserId(null);
        }

        // Microsoft user selection
        if (syncWithMicrosoftCalendar && microsoftUsers.length > 0) {
            const validMicrosoftUsers = microsoftUsers.filter(user => user.isValid);
            setSelectedMicrosoftUserId(validMicrosoftUsers.length > 0 ? validMicrosoftUsers[0]?._id : null);
        } else {
            setSelectedMicrosoftUserId(null);
        }
    }, [googleUsers, microsoftUsers, syncWithGoogleCalendar, syncWithMicrosoftCalendar]);

    useEffect(() => {
        // Check if values have actually changed
        const userSuggestionsString = JSON.stringify(userSuggestions);
        const userProfilesString = JSON.stringify(userProfiles);
        const eventOrganizerChanged = prevEventOrganizerRef.current !== eventOrganizer;
        const userSuggestionsChanged = prevUserSuggestionsRef.current !== userSuggestionsString;
        const userProfilesChanged = prevUserProfilesRef.current !== userProfilesString;
        
        // Only proceed if something actually changed
        if (!eventOrganizerChanged && !userSuggestionsChanged && !userProfilesChanged && prevEventOrganizerRef.current !== null) {
            return;
        }
        
        // Update refs
        prevEventOrganizerRef.current = eventOrganizer;
        prevUserSuggestionsRef.current = userSuggestionsString;
        prevUserProfilesRef.current = userProfilesString;
        
        if (eventOrganizer) {
            // Use userSuggestions for organizer lookup since it contains all users
            const organizer = Array.isArray(userSuggestions) ? userSuggestions.find((user) => user._id === eventOrganizer) : null
                || (Array.isArray(userProfiles) ? userProfiles.find((user) => user._id === eventOrganizer) : null);
            if (!syncWithGoogleCalendar && !syncWithMicrosoftCalendar) {
                if (organizer) {
                    setEventAttendees((prevAttendees) => {
                        // Only add organizer if not already in attendees
                        if (!prevAttendees.some((a) => a._id === eventOrganizer)) {
                            return [organizer, ...prevAttendees];
                        }
                        return prevAttendees;
                    });
                }
            } else {
                setEventAttendees((prevAttendees) => {
                    // Remove organizer if sync is enabled
                    if (prevAttendees.some((a) => a._id === eventOrganizer)) {
                        return prevAttendees.filter((a) => a._id !== eventOrganizer);
                    }
                    return prevAttendees;
                });
            }
        }
    }, [eventOrganizer, userSuggestions, userProfiles, syncWithGoogleCalendar, syncWithMicrosoftCalendar]);

    useEffect(() => {
        // Check if values have actually changed
        const userSuggestionsString = JSON.stringify(userSuggestions);
        const eventAttendeesString = JSON.stringify(safeEventAttendees);
        const externalAttendeesString = JSON.stringify(safeExternalAttendees);
        
        const attendeeInputChanged = prevAttendeeInputRef.current !== attendeeInput;
        const userSuggestionsChanged = prevUserSuggestionsRef.current !== userSuggestionsString;
        const eventAttendeesChanged = prevEventAttendeesRef.current !== eventAttendeesString;
        const externalAttendeesChanged = prevExternalAttendeesRef.current !== externalAttendeesString;
        
        // Only proceed if something actually changed
        if (!attendeeInputChanged && !userSuggestionsChanged && !eventAttendeesChanged && !externalAttendeesChanged && prevAttendeeInputRef.current !== null) {
            return;
        }
        
        // Update refs
        prevAttendeeInputRef.current = attendeeInput;
        prevUserSuggestionsRef.current = userSuggestionsString;
        prevEventAttendeesRef.current = eventAttendeesString;
        prevExternalAttendeesRef.current = externalAttendeesString;
        
        if (!attendeeInput) {
            setAttendeeSuggestions([]);
            return;
        }

        const selectedInternalIds = safeEventAttendees.map(a => a._id);
        const selectedExternalEmails = safeExternalAttendees.map(a => a.email.toLowerCase());

        const filtered = Array.isArray(userSuggestions) ? userSuggestions.filter((user) =>
            user.email.toLowerCase().includes(attendeeInput.toLowerCase()) &&
            !selectedInternalIds.includes(user._id)
        ) : [];

        const exactMatch = Array.isArray(userSuggestions) ? userSuggestions.find(
            (user) => user.email.toLowerCase() === attendeeInput.toLowerCase()
        ) : null;

        if (!exactMatch &&
            attendeeInput.includes('@') &&
            attendeeInput.includes('.') &&
            !selectedExternalEmails.includes(attendeeInput.toLowerCase())) {
            filtered.push({
                _id: null,
                email: attendeeInput,
                name: 'External Email',
                external: true,
            });
        }

        setAttendeeSuggestions(filtered);
    }, [attendeeInput, userSuggestions, eventAttendees, externalAttendees]);

    const handleSelectAttendee = (attendee) => {
        if (attendee.external) {
            if (!safeExternalAttendees.some((a) => a.email === attendee.email)) {
                setExternalAttendees([...safeExternalAttendees, {
                    email: attendee.email,
                    name: null,
                    status: 'pending',
                    reason: null,
                    response_date: null,
                    additional_notes: null,
                }]);
            }
        } else {
            if (!safeEventAttendees.some((a) => a._id === attendee._id)) {
                console.log("attendee", attendee);
                setEventAttendees([...safeEventAttendees, attendee]);
            }
        }
        setAttendeeInput('');
        setAttendeeSuggestions([]);
    };

    const handleRemoveAttendee = (idOrEmail, isExternal) => {
        if (!isExternal && idOrEmail === eventOrganizer) {
            return;
        }
        if (isExternal) {
            setExternalAttendees(safeExternalAttendees.filter((a) => a.email !== idOrEmail));
        } else {
            setEventAttendees(safeEventAttendees.filter((a) => a._id !== idOrEmail));
        }
    };

    const handleToggleRepeatTypeDropdown = () => {
        setRepeatTypeDropdownOpen(!repeatTypeDropdownOpen);
    };

    const handleToggleOrganizerDropdown = () => {
        setOrganizerDropdownOpen(!organizerDropdownOpen);
    };

    const handleToggleReminderMethodDropdown = () => {
        setReminderMethodDropdownOpen((prev) => !prev);
    };

    const handleEventCreateDateSelect = (date) => {
        setEventDate(date);
        setEventDateIsValid(false);

        // Update eventStartTime if a valid time string exists
        if (startTimeString) {
            const [startHour, startMinute] = startTimeString.split(":").map(Number);
            if (!isNaN(startHour) && !isNaN(startMinute)) {
                const updatedStart = moment(date).set({ hour: startHour, minute: startMinute }).toDate();
                setEventStartTime(updatedStart);
            }
        }

        // Update eventEndTime if a valid time string exists
        if (endTimeString) {
            const [endHour, endMinute] = endTimeString.split(":").map(Number);
            if (!isNaN(endHour) && !isNaN(endMinute)) {
                const updatedEnd = moment(date).set({ hour: endHour, minute: endMinute }).toDate();
                setEventEndTime(updatedEnd);
            }
        }
    };

    const handleCloseCreateEventModal = () => {
        if (!isSubmitLoading) {
            // Use the setter function which will be wrapped by parent to handle closing state
            setCreateEventModalOpen(false);
            // setDateRange([null, null]);
            // setDateRangeType("single");
            setEventDate(null);
            setEventEndDate(null);
            setStartTime("");
            setEndTime("");
            setEventStartTime("");
            setEventEndTime("");
            setStartTimeString("");
            setEndTimeString("");
            // setDateRangeIsValid(false);
            setStartTimeIsValid(false);
            setEndTimeIsValid(false);
            setEventName("");
            setEventDescription("");
            setEventLocation("");
            setEventOrganizer(user._id);
            setEventAttendees([]);
            setExternalAttendees([]);
            setEventBanner(null);
            setIsVirtual(false);
            setEventMeetingLink("");
            setRepeatType("does_not_repeat");
            setEventNameIsValid(false);
            setEventDateIsValid(false);
            setEventStartTimeIsValid(false);
            setEventEndTimeIsValid(false);
            setRepeatTypeIsValid(false);
            setOrganizerDropdownOpen(false);
            setRepeatTypeDropdownOpen(false);
            setSyncWithGoogleCalendar(false);
            setSyncWithMicrosoftCalendar(false);
            setReminderMethod("popup");
            document.body.classList.remove("ReactModal__Body--open");
        }
    };

    const handleEventSubmit = async (e) => {
        e.preventDefault();
        setEventNameIsValid(false);
        setEventDateIsValid(false);
        setEventStartTimeIsValid(false);
        setEventEndTimeIsValid(false);
        setRepeatTypeIsValid(false);
        setEventEndDateIsValid(false);
        let isValid = true;

        if (!eventName) {
            setEventNameIsValid(true);
            eventNameRef.current?.focus();
            isValid = false;
        }
        if (!eventDate) {
            setEventDateIsValid(true);
            eventDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (!repeatType) {
            setRepeatTypeIsValid(true);
            repeatTypeRef.current?.focus();
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && !eventEndDate) {
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && eventDate && eventEndDate && moment(eventEndDate).isBefore(moment(eventDate), "day")) {
            toast.error("End date must be on or after the event date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.focus();
            isValid = false;
        }
        if (repeatType !== "all_day") {
            if (!eventStartTime) {
                setEventStartTimeIsValid(true);
                eventStartTimeRef.current?.focus();
                isValid = false;
            }
            if (!eventEndTime) {
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
            if (eventStartTime && eventEndTime && moment(eventStartTime, "HH:mm").isSameOrAfter(moment(eventEndTime, "HH:mm"))) {
                toast.error("Start time must be before end time!", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setEventStartTimeIsValid(true);
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
        }
        if ((syncWithGoogleCalendar || syncWithMicrosoftCalendar) && reminderMinutes < 0) {
            toast.error("Reminder minutes cannot be negative!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            isValid = false;
        }
        if ((syncWithGoogleCalendar || syncWithMicrosoftCalendar) && reminderMinutes > 40320) {
            toast.error("Reminder cannot be more than 4 weeks (40320 minutes) before the event!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            isValid = false;
        }

        if ((isNaN(reminderMinutes) || reminderMinutes < 0)) {
            setReminderMinutesIsValid(true);
            isValid = false;
        } else {
            setReminderMinutesIsValid(false);
        }

        if (!isValid) {
            toast.error("Please fill all required fields!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        try {
            setIsSubmitLoading(true);
            let isMicrosoftCalendarEnabled = false;
            let isMicrosoftTokenValid = false;
            if (syncWithMicrosoftCalendar) {
                isMicrosoftCalendarEnabled = await checkMicrosoftCalendarEnabledStatus(token, user._id);
                if (!isMicrosoftCalendarEnabled) {
                    toast.error("Microsoft Calendar integration is not enabled!", {
                        position: "bottom-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    setIsMicrosoftTokenInvalidModalOpen(true);
                    return;
                }
                isMicrosoftTokenValid = await checkMicrosoftTokenValidity(token, user, setIsMicrosoftTokenChecking);
                if (!isMicrosoftTokenValid) {
                    toast.error("Microsoft Calendar token is invalid!", {
                        position: "bottom-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    setIsMicrosoftTokenInvalidModalOpen(true);
                    return;
                }
            }

            const googleAttendees = syncWithGoogleCalendar
                ? [
                    ...safeEventAttendees.map((attendee) => {
                        const profile = userProfiles.find((u) => u._id === attendee._id);
                        return profile ? { email: profile.email } : null;
                    }).filter(Boolean),
                    ...safeExternalAttendees.map((attendee) => ({ email: attendee.email }))
                ]
                : [];

            const microsoftAttendees = syncWithMicrosoftCalendar
                ? [
                    ...safeEventAttendees.map((attendee) => {
                        const profile = userProfiles.find((u) => u._id === attendee._id);
                        return profile ? {
                            emailAddress: {
                                address: profile.email,
                                name: profile.first_name ? `${profile.first_name} ${profile.last_name || ''}` : profile.company_name || profile.email
                            }
                        } : null;
                    }).filter(Boolean),
                    ...safeExternalAttendees.map((attendee) => ({
                        emailAddress: {
                            address: attendee.email,
                            name: attendee.name || attendee.email
                        }
                    }))
                ]
                : [];

            const endpoint = syncWithMicrosoftCalendar
                ? '/api/microsoft-calendar/events/create'
                : syncWithGoogleCalendar
                    ? '/api/google-calendar/events/create'
                    : '/api/events/create';
            const requestData = syncWithMicrosoftCalendar
                ? {
                    summary: eventName,
                    start: {
                        dateTime: eventStartTime ? moment(eventStartTime).toISOString() : moment(eventDate).startOf('day').toISOString(),
                        timeZone: global.config.getTimeZone() || 'India Standard Time',
                    },
                    end: {
                        dateTime: eventEndTime ? moment(eventEndTime).toISOString() : (eventStartTime ? moment(eventStartTime).add(1, 'hour').toISOString() : moment(eventDate).endOf('day').toISOString()),
                        timeZone: global.config.getTimeZone() || 'India Standard Time',
                    },
                    location: eventLocation || null,
                    description: eventDescription || null,
                    attendees: microsoftAttendees,
                    reminders: {
                        overrides: [
                            {
                                method: reminderMethod || 'email',
                                minutes: reminderMinutes >= 0 ? reminderMinutes : 15,
                            },
                        ],
                    },
                    // recurrence: ["daily", "weekly", "monthly"].includes(repeatType) && eventEndDate
                    //     ? [`RRULE:FREQ=${repeatType.toUpperCase()};UNTIL=${moment(eventEndDate).format('YYYYMMDD')}`]
                    //     : null,
                    recurrence: ["daily", "weekly", "monthly", "yearly"].includes(repeatType) && eventEndDate
                        ? [`RRULE:FREQ=${repeatType.toUpperCase()};UNTIL=${moment(eventEndDate).format('YYYYMMDD')}`]
                        : ["daily", "weekly", "monthly", "yearly"].includes(repeatType) && repeatType === "yearly" && !eventEndDate
                            ? [`RRULE:FREQ=${repeatType.toUpperCase()};UNTIL=${moment(eventStartTime || eventDate).add(5, 'years').format('YYYYMMDD')}`]
                            : null,
                    isVirtual,
                    selectedMicrosoftUserId,
                }
                : syncWithGoogleCalendar
                    ? {
                        summary: eventName,
                        start: {
                            dateTime: eventStartTime ? moment(eventStartTime).toISOString() : moment(eventDate).startOf('day').toISOString(),
                            timeZone: global.config.getTimeZone(),
                        },
                        end: {
                            dateTime: eventEndTime ? moment(eventEndTime).toISOString() : (eventStartTime
                                ? moment(eventStartTime).add(1, 'hour').toISOString()
                                : moment(eventDate).endOf('day').toISOString()),
                            timeZone: global.config.getTimeZone(),
                        },
                        location: eventLocation || null,
                        description: eventDescription || null,
                        attendees: googleAttendees,
                        reminders: {
                            useDefault: false,
                            overrides: [{ method: reminderMethod, minutes: reminderMinutes }],
                        },
                        recurrence: ["daily", "weekly", "monthly"].includes(repeatType) && eventEndDate
                            ? [`RRULE:FREQ=${repeatType.toUpperCase()};UNTIL=${moment(eventEndDate).format('YYYYMMDD')}`]
                            : null,
                        isVirtual,
                        selectedGoogleUserId,
                    }
                    : {
                        event_name: eventName,
                        event_date: moment(eventDate).format("YYYY-MM-DD"),
                        end_date:
                            ["daily", "weekly", "monthly"].includes(repeatType) && eventEndDate
                                ? moment(eventEndDate).format("YYYY-MM-DD")
                                : null,
                        start_date_time:
                            eventStartTime && eventEndTime && repeatType !== "all_day"
                                ? moment(eventStartTime).toISOString()
                                : null,
                        end_date_time:
                            eventStartTime && eventEndTime && repeatType !== "all_day"
                                ? moment(eventEndTime).toISOString()
                                : null,
                        event_description: eventDescription || null,
                        event_location: eventLocation || null,
                        event_organizer: eventOrganizer,
                        // event_attendees: eventAttendees.length > 0 ? eventAttendees.map((a) => a._id) : [],
                        event_attendees: safeEventAttendees.length > 0 ? safeEventAttendees.map((a) => ({
                            user: a._id,
                            status: 'pending',
                            reason: null,
                            response_date: null,
                            additional_notes: null
                        })) : [],
                        external_attendees: safeExternalAttendees.length > 0 ? safeExternalAttendees.map((a) => ({
                            email: a.email,
                            name: a.name || null,
                            status: 'pending',
                            reason: null,
                            response_date: null,
                            additional_notes: null
                        })) : [],
                        event_is_virtual: isVirtual,
                        event_meeting_link: isVirtual && eventMeetingLink ? eventMeetingLink : null,
                        repeat_type: repeatType,
                        timeZone: global.config.getTimeZone(),
                        sync_with_google_calendar: syncWithGoogleCalendar,
                        sync_with_microsoft_calendar: syncWithMicrosoftCalendar,
                        google_reminders: syncWithGoogleCalendar ? {
                            method: reminderMethod,
                            minutes: reminderMinutes,
                        } : null,
                        microsoft_reminders: syncWithMicrosoftCalendar ? {
                            method: reminderMethod,
                            minutes: reminderMinutes
                        } : null,
                        google_attendees: syncWithGoogleCalendar ? googleAttendees : [],
                        microsoft_attendees: syncWithMicrosoftCalendar ? microsoftAttendees : [],
                    };

            let config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            let payload = requestData;
            if (!syncWithGoogleCalendar && !syncWithMicrosoftCalendar && eventBannerFile) {
                // Use FormData only when a file is included
                const formData = new FormData();
                Object.entries(requestData).forEach(([key, value]) => {
                    if (key === "event_attendees" && Array.isArray(value)) {
                        // value.forEach((attendeeId, index) => {
                        //     formData.append(`event_attendees[${index}]`, attendeeId);
                        // });
                        value.forEach((attendee, index) => {
                            formData.append(`event_attendees[${index}][user]`, attendee.user);
                            formData.append(`event_attendees[${index}][status]`, attendee.status);
                            if (attendee.reason) formData.append(`event_attendees[${index}][reason]`, attendee.reason);
                            if (attendee.response_date) formData.append(`event_attendees[${index}][response_date]`, attendee.response_date);
                            if (attendee.additional_notes) formData.append(`event_attendees[${index}][additional_notes]`, attendee.additional_notes);
                        });
                    } else if (key === "external_attendees" && Array.isArray(value)) {
                        value.forEach((attendee, index) => {
                            formData.append(`external_attendees[${index}][email]`, attendee.email);
                            if (attendee.name) formData.append(`external_attendees[${index}][name]`, attendee.name);
                            formData.append(`external_attendees[${index}][status]`, attendee.status);
                            if (attendee.reason) formData.append(`external_attendees[${index}][reason]`, attendee.reason);
                            if (attendee.response_date) formData.append(`external_attendees[${index}][response_date]`, attendee.response_date);
                            if (attendee.additional_notes) formData.append(`external_attendees[${index}][additional_notes]`, attendee.additional_notes);
                        });
                    } else if (value !== null) {
                        formData.append(key, value);
                    }
                });
                // for (let key in requestData) {
                //     formData.append(key, requestData[key]);
                // }
                formData.append("event_banner", eventBannerFile);
                payload = formData;
                config.headers["Content-Type"] = "multipart/form-data";
            }

            await axios.post(endpoint, payload, config);

            global.config.activityLog(
                window.location.href,
                "Event Creation",
                `Event "${eventName}" created successfully${syncWithMicrosoftCalendar ? ' in Microsoft Calendar' : syncWithGoogleCalendar ? ' in Google Calendar' : ''}`
            );
            toast.success(`Event created successfully${syncWithMicrosoftCalendar ? ' in Microsoft Calendar' : syncWithGoogleCalendar ? ' in Google Calendar' : ''}`, {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            refetchEvents();
            if (syncWithGoogleCalendar) refetchSelectedGoogleEvents();
            if (syncWithMicrosoftCalendar) refetchSelectedMicrosoftEvents();
            handleCloseCreateEventModal();
        } catch (error) {
            console.error("Error creating event:", error);
            global.config.slackMessage(error.toString());

            if (error.response?.data?.error?.code === 'ErrorAccessDenied') {
                toast.error('Access denied. Please re-authenticate your Microsoft account.', {
                    position: "bottom-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setIsMicrosoftTokenInvalidModalOpen(true);
            } else {
                toast.error(error.response?.data?.message || "Something went wrong", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleClickOutsideDropdown = (event) => {
        const refs = {
            reminder: reminderMethodRef.current,
            repeatType: repeatTypeRef.current,
            organizer: organizerRef.current,
        };

        // Log any null refs for debugging
        Object.entries(refs).forEach(([key, ref]) => {
            if (!ref) {
                console.warn(`Ref ${key} is null`);
            }
        });

        try {
            if (refs.reminder && !refs.reminder.contains(event.target)) {
                setReminderMethodDropdownOpen(false);
            }
            if (refs.repeatType && !refs.repeatType.contains(event.target)) {
                setRepeatTypeDropdownOpen(false);
            }
            if (refs.organizer && !refs.organizer.contains(event.target)) {
                setOrganizerDropdownOpen(false);
            }
        } catch (err) {
            console.error('Error in handleClickOutsideDropdown:', err);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined' || !document) {
            return;
        }

        document.addEventListener('click', handleClickOutsideDropdown);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, []);

    return (
        <Modal
            className={`client-modal sprint-modal time-off-close calendar-event-create ${isSlotSelected ? 'calendar-event-center' : ''}`}
            overlayClassName="calendar-event-create-overlay"
            isOpen={createEventModalOpen}
            onRequestClose={!isSubmitLoading ? handleCloseCreateEventModal : undefined}
            onAfterClose={() => setIsSlotSelected(false)}
            contentLabel="Request Time Off or Create Event"
            closeTimeoutMS={480}
            shouldCloseOnOverlayClick={!isSubmitLoading}
            shouldCloseOnEsc={!isSubmitLoading}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 100001,
                },
                content: {
                    height: "41%",
                    width: "30%",
                    position: "fixed",
                    border: 'none',
                    borderRadius: '15px',
                    padding: 0,
                    overflow: 'auto',
                    zIndex: 100002,
                },
            }}
        >
            <div className="modal timeoff-modal">
                        <div>
                            <div className="modal-client-head">
                                <h2>Create Event</h2>
                                <div className="modal-close-invite outter-modal-main">
                                    <a onClick={handleCloseCreateEventModal} disabled={isSubmitLoading}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <div className="form-field-client-outer">
                                <style>
                                    {`
                                        .ReactModal__Content.ReactModal__Content--after-open.calendar-event-create .form-field-client .suggession-user {
                                            width: 100% !important;
                                        }
                                        .ReactModal__Content.ReactModal__Content--after-open.calendar-event-create .form-field-client .selected-guests {
                                            width: 100% !important;
                                        }
                                    `}
                                </style>
                                <form onSubmit={handleEventSubmit}>
                                    {isGoogleSyncVisible && (
                                        <div className="form-field-client is-virtual-vent">
                                            <label className="form-label" htmlFor="syncGoogle">Sync with Google Calendar</label>
                                            <input
                                                type="checkbox"
                                                checked={syncWithGoogleCalendar}
                                                onChange={(e) => {
                                                    setSyncWithGoogleCalendar(e.target.checked);
                                                    if (e.target.checked) setSyncWithMicrosoftCalendar(false);
                                                }}
                                                className="form-checkbox"
                                                id="syncGoogle"
                                            />
                                        </div>
                                    )}
                                    {isGoogleSyncVisible && syncWithGoogleCalendar && googleUsers.filter(user => user.isValid).length > 1 && (
                                        <div className="form-field-client google-users-selection">
                                            <label className="form-label">Select Google Account</label>
                                            <div className="google-users-selection-inner">
                                                {googleUsers.map((user) => (
                                                    <div key={user._id} className="google-users-selection-inner-input">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGoogleUserId === user._id}
                                                            onChange={() => setSelectedGoogleUserId(user._id)}
                                                            className="form-checkbox"
                                                            id={`google-user-${user._id}`}
                                                        />
                                                        <label htmlFor={`google-user-${user._id}`} className="form-label">
                                                            {user.syncedGoogleEmail || 'Unknown Google Email'}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {isMicrosoftSyncVisible && (
                                        <div className="form-field-client is-virtual-vent">
                                            <label className="form-label" htmlFor="syncMicrosoft">Sync with Microsoft Calendar</label>
                                            <input
                                                type="checkbox"
                                                checked={syncWithMicrosoftCalendar}
                                                onChange={(e) => {
                                                    setSyncWithMicrosoftCalendar(e.target.checked);
                                                    if (e.target.checked) setSyncWithGoogleCalendar(false); // Ensure mutual exclusivity
                                                }}
                                                className="form-checkbox"
                                                id="syncMicrosoft"
                                            />
                                        </div>
                                    )}
                                    {isMicrosoftSyncVisible && syncWithMicrosoftCalendar && microsoftUsers.filter(user => user.isValid).length > 1 && (
                                        <div className="form-field-client google-users-selection">
                                            <label className="form-label">Select Microsoft Account</label>
                                            <div className="google-users-selection-inner">
                                                {microsoftUsers.filter(user => user.isValid).map((user) => (
                                                    <div key={user._id} className="google-users-selection-inner-input">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMicrosoftUserId === user._id}
                                                            onChange={() => setSelectedMicrosoftUserId(user._id)}
                                                            className="form-checkbox"
                                                            id={`microsoft-user-${user._id}`}
                                                        />
                                                        <label htmlFor={`microsoft-user-${user._id}`} className="form-label">
                                                            {user.syncedMicrosoftEmail || 'Unknown Microsoft Email'}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client">
                                        <label htmlFor="eventName" className="form-label">
                                            Event Name *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="eventName"
                                                type="text"
                                                value={eventName}
                                                onChange={(e) => setEventName(e.target.value)}
                                                className={eventNameIsValid ? "input-validation-error form-input" : "form-input"}
                                                ref={eventNameRef}
                                                placeholder="Enter the name for event"
                                            />
                                            {eventNameIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Event name is required
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field-client">
                                        <label htmlFor="repeatType" className="form-label">
                                            Repeat Type *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={repeatTypeRef} className={`cursor ${repeatTypeDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="repeat-type-select"
                                                    onClick={handleToggleRepeatTypeDropdown}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${repeatTypeIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {repeatType ? REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label : 'Select'}
                                                </span>
                                                {repeatTypeDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {REPEAT_TYPES.map((rt) => (
                                                                <li
                                                                    key={rt.value}
                                                                    className={repeatType === rt.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setRepeatType(rt.value);
                                                                        setRepeatTypeDropdownOpen(false);
                                                                        if (rt.value === "all_day") {
                                                                            setEventStartTime("");
                                                                            setEventEndTime("");
                                                                        }
                                                                        if (!["daily", "weekly", "monthly"].includes(rt.value)) {
                                                                            setEventEndDate(null);
                                                                        }
                                                                    }}
                                                                >
                                                                    {rt.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {repeatTypeIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Repeat type is required
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field-client">
                                        <label htmlFor="eventDate" className="form-label">
                                            {["daily", "weekly", "monthly"].includes(repeatType) ? "Start Date *" : "Event Date *"}
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div className="date-picker-outer date-picker-outer-all">
                                                <div className="custoom-icon-calender custoom-icon-calender-all">
                                                    <div ref={eventDateRef}>
                                                        <DatePicker
                                                            oneTap
                                                            format="dd/MM/yyyy"
                                                            placeholder="Select Date"
                                                            placement="topEnd"
                                                            ranges={ranges}
                                                            className="project-calender time-off-cal"
                                                            value={eventDate}
                                                            onChange={handleEventCreateDateSelect}
                                                            isoWeek={appSettings.weekStartDay === 'Monday'}
                                                            disabled={isSlotSelected}
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {eventDateIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Select the event date range
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {["daily", "weekly", "monthly"].includes(repeatType) && (
                                        <div className="form-field-client">
                                            <label htmlFor="eventEndDate" className="form-label">
                                                End Date *
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all">
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={eventEndDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select End Date"
                                                                placement="topEnd"
                                                                ranges={ranges}
                                                                className="project-calender time-off-cal"
                                                                value={eventEndDate}
                                                                onChange={(date) => setEventEndDate(date)}
                                                                isoWeek={appSettings.weekStartDay === 'Monday'}
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(eventDate), 'day')}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {eventEndDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        Select the event end date
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {repeatType !== "all_day" && (
                                        <div className="form-field-client radio-sec">
                                            <div className="form-field-client">
                                                <label htmlFor="eventStartTime" className="form-label">
                                                    Start Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="startTime"
                                                        type="time"
                                                        // value={startTimeString}
                                                        // onChange={(e) => {
                                                        //     const value = e.target.value;
                                                        //     setStartTimeString(value);
                                                        //     const [hours, minutes] = value.split(":").map(Number);
                                                        //     if (!isNaN(hours) && !isNaN(minutes)) {
                                                        //         const baseDate = eventDate || new Date();
                                                        //         const updatedStart = moment(baseDate).set({ hour: hours, minute: minutes }).toDate();
                                                        //         setEventStartTime(updatedStart);
                                                        //     }
                                                        // }}
                                                        value={eventStartTime && moment(eventStartTime).isValid() ? moment(eventStartTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const baseDate = eventDate || new Date();
                                                            const updatedStart = moment(baseDate).set({ hour: hours, minute: minutes }).toDate();
                                                            setEventStartTime(updatedStart);
                                                        }}
                                                        className={startTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventStartTimeRef}
                                                    />
                                                    {eventStartTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid start time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-field-client">
                                                <label htmlFor="eventEndTime" className="form-label">
                                                    End Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="endTime"
                                                        type="time"
                                                        // value={endTimeString}
                                                        // onChange={(e) => {
                                                        //     const value = e.target.value;
                                                        //     setEndTimeString(value);
                                                        //     const [hours, minutes] = e.target.value.split(":").map(Number);
                                                        //     if (!isNaN(hours) && !isNaN(minutes)) {
                                                        //         const baseDate = eventDate || new Date();
                                                        //         const updatedEnd = moment(baseDate).set({ hour: hours, minute: minutes }).toDate();
                                                        //         setEventEndTime(updatedEnd);
                                                        //     }
                                                        // }}
                                                        value={eventEndTime && moment(eventEndTime).isValid() ? moment(eventEndTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            const baseDate = eventDate || new Date();
                                                            const updatedEnd = moment(baseDate).set({ hour: hours, minute: minutes }).toDate();
                                                            setEventEndTime(updatedEnd);
                                                        }}
                                                        className={endTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventEndTimeRef}
                                                    />
                                                    {eventEndTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid end time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    
                                    {!isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="location" className="form-label">
                                                Location
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <input
                                                    id="location"
                                                    type="text"
                                                    value={eventLocation}
                                                    onChange={(e) => setEventLocation(e.target.value)}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client is-virtual-vent">
                                        <label htmlFor="isVirtual" className="form-label">Virtual Event</label>
                                        <input
                                            id="isVirtual"
                                            type="checkbox"
                                            checked={isVirtual}
                                            onChange={(e) => setIsVirtual(e.target.checked)}
                                            className="form-checkbox"
                                        />
                                    </div>
                                    {isVirtual && !syncWithGoogleCalendar && !syncWithMicrosoftCalendar && (
                                        <div className="form-field-client">
                                            <label htmlFor="meetingLink" className="form-label">
                                                Meeting Link
                                            </label>
                                            <input
                                                id="meetingLink"
                                                type="url"
                                                value={eventMeetingLink}
                                                onChange={(e) => setEventMeetingLink(e.target.value)}
                                                className="form-input"
                                                placeholder="https://zoom.us/j/..."
                                                ref={eventMeetingLinkRef}
                                            />
                                        </div>
                                    )}
                                    {(!syncWithGoogleCalendar && !syncWithMicrosoftCalendar && (user.userType === SUPER_ADMIN || user.userType === ADMIN)) && (
                                        <div className="form-field-client">
                                            <label htmlFor="organizer" className="form-label">
                                                Organizer
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div ref={organizerRef} className={`cursor ${organizerDropdownOpen ? 'open' : ''}`}>
                                                    <span
                                                        id="organizer-select"
                                                        onClick={handleToggleOrganizerDropdown}
                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                    >
                                                        {eventOrganizer
                                                            ? userProfiles.find((u) => u._id === eventOrganizer)?.first_name
                                                                ? `${userProfiles.find((u) => u._id === eventOrganizer)?.first_name} ${userProfiles.find((u) => u._id === eventOrganizer)?.last_name}`
                                                                : `${userProfiles.find((u) => u._id === eventOrganizer)?.company_name || "Current User"}`
                                                            : 'Select Organizer'}
                                                    </span>
                                                    {organizerDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            {userProfileLoading ? (
                                                                <ul className="client-list">
                                                                    {Array.from({ length: 5 }).map((_, idx) => (
                                                                        <li key={idx} style={{ padding: '8px 12px', cursor: 'default' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                                <div
                                                                                    style={{
                                                                                        width: '20px',
                                                                                        height: '20px',
                                                                                        borderRadius: '50%',
                                                                                        backgroundColor: '#e5e7eb',
                                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                                    }}
                                                                                />
                                                                                <div
                                                                                    style={{
                                                                                        width: `${120 + idx * 10}px`,
                                                                                        height: '18px',
                                                                                        backgroundColor: '#e5e7eb',
                                                                                        borderRadius: '4px',
                                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <ul className="client-list">
                                                                    {userProfiles
                                                                        .slice()
                                                                        .sort((a, b) => {
                                                                            const nameA = a.first_name
                                                                                ? `${a.first_name} ${a.last_name}`
                                                                                : a.company_name;
                                                                            const nameB = b.first_name
                                                                                ? `${b.first_name} ${b.last_name}`
                                                                                : b.company_name;
                                                                            return nameA.localeCompare(nameB);
                                                                        })
                                                                        .map((item) => (
                                                                            <li
                                                                                key={item._id}
                                                                                className={eventOrganizer === item._id ? 'selected-item' : ''}
                                                                                onClick={() => {
                                                                                    setEventOrganizer(item._id);
                                                                                    setOrganizerDropdownOpen(false);
                                                                                }}
                                                                            >
                                                                                {item.first_name
                                                                                    ? `${item.first_name} ${item.last_name}`
                                                                                    : `${item.company_name || ''} (Company)`}
                                                                            </li>
                                                                        ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-field-client guest-input-wrapper">
                                        <label className="form-label">Guests</label>
                                        <div className="suggession-user">
                                            <input
                                                type="text"
                                                value={attendeeInput}
                                                onChange={(e) => setAttendeeInput(e.target.value)}
                                                placeholder="Add guests"
                                                className="form-input"
                                                disabled={userSuggestionsLoading}
                                            />
                                            {userSuggestionsLoading ? (
                                                <ul className="suggestion-dropdown">
                                                    {Array.from({ length: 3 }).map((_, idx) => (
                                                        <li key={idx} style={{ padding: '8px 12px', cursor: 'default' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div
                                                                    style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#e5e7eb',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        width: `${150 + idx * 20}px`,
                                                                        height: '18px',
                                                                        backgroundColor: '#e5e7eb',
                                                                        borderRadius: '4px',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                attendeeSuggestions.length > 0 && (
                                                    <ul className="suggestion-dropdown">
                                                        {attendeeSuggestions.map((user, idx) => (
                                                            <li key={user._id || `external-${idx}`} onClick={() => handleSelectAttendee(user)}>
                                                                <span className="email-icon">
                                                                    <img
                                                                        src={`/uploads/lowProfileImage/${user.profile_image}`}
                                                                        alt="Email"
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = "/uploads/lowProfileImage/user_default.png";
                                                                        }}
                                                                    />
                                                                </span>
                                                                <span>{user.email}</span>
                                                                {user.external && <em> (external)</em>}
                                                                {user._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )
                                            )}
                                        </div>
                                    </div>
                                    
                                    {(safeEventAttendees.length > 0 || safeExternalAttendees.length > 0) && (
                                        <div className="form-field-client guest-input-wrapper hide-it">
                                            <label className="form-label"></label>
                                            <div className="selected-guests">
                                                {safeEventAttendees.map((attendee) => (
                                                    <div key={attendee._id} className="guest-chip">
                                                        <span className={`email-icon ${attendee._id === eventOrganizer ? 'organizer-icon' : ''}`}>
                                                            <img
                                                                src={`/uploads/lowProfileImage/${attendee.profile_image}`}
                                                                alt="Email"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/uploads/lowProfileImage/user_default.png";
                                                                }}
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span className={`attendee-name ${attendee._id === eventOrganizer ? 'organizer-name' : ''}`}>{attendee.first_name ? `${attendee.first_name} ${attendee.last_name || ''}` : attendee.company_name || attendee.email}</span>
                                                            {attendee._id === eventOrganizer && <em> (Organizer)</em>}
                                                            {attendee._id !== eventOrganizer && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAttendee(attendee._id, false)}
                                                                    className=""
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                                {safeExternalAttendees.map((attendee, i) => (
                                                    <div key={`external-${i}`} className="guest-chip">
                                                        <span className="email-icon">
                                                            <img src={dummyImage} alt="Email" />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span>{attendee.email} (external)</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveAttendee(attendee.email, true)}
                                                                className=""
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field-client">
                                        <label htmlFor="description" className="form-label">
                                            Description
                                        </label>
                                        <div className="inner-sec-cal-form">
                                        <textarea
                                            id="description"
                                            name="description"
                                            className="form-textarea"
                                            rows="3"
                                            value={eventDescription}
                                            onChange={(e) => setEventDescription(e.target.value)}
                                            placeholder="Write here"
                                        ></textarea>
                                        </div>
                                    </div>
                                    {syncWithGoogleCalendar && (
                                        <div className="form-field-client-outer-reminder">
                                            <label className="form-label reminder-heading" htmlFor="reminder-settings">
                                                Reminder Settings
                                                <span className="reminder-info" title="Choose how and when to be reminded about this event.">
                                                    <img src={infoIcon} alt="Info Icon" />
                                                </span>
                                            </label>

                                            <div className="form-field-client">

                                                <label htmlFor="reminder-method" className="form-label">
                                                    Reminder Method
                                                </label>
                                                <div ref={reminderMethodRef} className={`cursor ${reminderMethodDropdownOpen ? 'open' : ''}`}>
                                                    <span
                                                        id="reminder-method"
                                                        onClick={handleToggleReminderMethodDropdown}
                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-haspopup="listbox"
                                                        aria-expanded={reminderMethodDropdownOpen}
                                                        aria-describedby="reminder-method-desc"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleToggleReminderMethodDropdown();
                                                            }
                                                        }}
                                                    >
                                                        {REMINDER_METHODS.find((method) => method.value === reminderMethod)?.label || 'Select Method'}
                                                    </span>
                                                    {reminderMethodDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            <ul className="client-list" role="listbox">
                                                                {REMINDER_METHODS.map((method) => (
                                                                    <li
                                                                        key={method.value}
                                                                        className={reminderMethod === method.value ? 'selected-item' : ''}
                                                                        onClick={() => {
                                                                            setReminderMethod(method.value);
                                                                            setReminderMethodDropdownOpen(false);
                                                                        }}
                                                                        role="option"
                                                                        aria-selected={reminderMethod === method.value}
                                                                        tabIndex={0}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                setReminderMethod(method.value);
                                                                                setReminderMethodDropdownOpen(false);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {method.label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    <p id="reminder-method-desc" className="tooltip-calender-event">
                                                        {reminderMethod === 'popup' ? 'Shows an in-app alert.' : 'Sends an email to attendees.'}
                                                    </p>
                                                </div>

                                            </div>

                                            <div className="form-field-client">
                                                <label htmlFor="reminder-time" className="sr-only">
                                                    Reminder Time (minutes before)
                                                </label>
                                                {/* <select
                                                            id="reminder-time"
                                                            value={reminderMinutes}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (value === 'custom') {
                                                                    setReminderMinutes(0);
                                                                    setShowCustomReminder(true);
                                                                } else {
                                                                    setReminderMinutes(parseInt(value));
                                                                    setShowCustomReminder(false);
                                                                }
                                                            }}
                                                            className="form-input w-full"
                                                            aria-describedby="reminder-time-desc"
                                                        >
                                                            <option value="5">5 minutes before</option>
                                                            <option value="15">15 minutes before</option>
                                                            <option value="30">30 minutes before</option>
                                                            <option value="60">1 hour before</option>
                                                            <option value="1440">1 day before</option>
                                                            <option value="custom">Custom</option>
                                                        </select> */}
                                                <div className="form-field-inner-sec inner-sec-cal-form">
                                                    <input
                                                        type="number"
                                                        value={reminderMinutes}
                                                        onChange={(e) => setReminderMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                                        min="0"
                                                        placeholder="Minutes before"
                                                        className="form-input w-1/2"
                                                    />
                                                    <p id="reminder-time-desc" className="tooltip-calender-event">
                                                        Select when to receive the reminder.
                                                    </p>
                                                </div>
                                                {reminderMinutesIsValid && (
                                                    <span id="custom-reminder-error" className="text-red-500 text-xs italic">
                                                        Please enter a valid number of minutes (0 or greater)
                                                    </span>
                                                )}
                                            </div>

                                        </div>
                                    )}
                                    {syncWithMicrosoftCalendar && (
                                        <div className="form-field-client-outer-reminder">
                                            <label className="form-label reminder-heading" htmlFor="reminder-settings">
                                                Reminder Settings
                                                <span className="reminder-info" title="Choose how and when to be reminded about this event.">
                                                    <img src={infoIcon} alt="Info Icon" />
                                                </span>
                                            </label>


                                            <div className="form-field-client">
                                                <label htmlFor="reminder-method" className="form-label">
                                                    Reminder Method
                                                </label>
                                                <div ref={reminderMethodRef} className={`cursor ${reminderMethodDropdownOpen ? 'open' : ''}`}>
                                                    <span
                                                        id="reminder-method"
                                                        onClick={handleToggleReminderMethodDropdown}
                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-haspopup="listbox"
                                                        aria-expanded={reminderMethodDropdownOpen}
                                                        aria-describedby="reminder-method-desc"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                handleToggleReminderMethodDropdown();
                                                            }
                                                        }}
                                                    >
                                                        {REMINDER_METHODS.find((method) => method.value === reminderMethod)?.label || 'Select Method'}
                                                    </span>
                                                    {reminderMethodDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            <ul className="client-list" role="listbox">
                                                                {REMINDER_METHODS.map((method) => (
                                                                    <li
                                                                        key={method.value}
                                                                        className={reminderMethod === method.value ? 'selected-item' : ''}
                                                                        onClick={() => {
                                                                            setReminderMethod(method.value);
                                                                            setReminderMethodDropdownOpen(false);
                                                                        }}
                                                                        role="option"
                                                                        aria-selected={reminderMethod === method.value}
                                                                        tabIndex={0}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                setReminderMethod(method.value);
                                                                                setReminderMethodDropdownOpen(false);
                                                                            }
                                                                        }}
                                                                    >
                                                                        {method.label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    <p id="reminder-method-desc" className="tooltip-calender-event">
                                                        {reminderMethod === 'popup' ? 'Shows an in-app alert.' : 'Sends an email to attendees.'}
                                                    </p>
                                                </div>

                                            </div>

                                            <div className="form-field-client">
                                                <label htmlFor="reminder-time" className="sr-only">
                                                    Reminder Time (minutes before)
                                                </label>
                                                <div className="form-field-inner-sec inner-sec-cal-form">
                                                    <input
                                                        type="number"
                                                        value={reminderMinutes}
                                                        onChange={(e) => setReminderMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                                                        min="0"
                                                        placeholder="Minutes before"
                                                        className="form-input w-1/2"
                                                    />

                                                    {reminderMinutesIsValid && (
                                                        <span id="custom-reminder-error" className="text-red-500 text-xs italic">
                                                            Please enter a valid number of minutes (0 or greater)
                                                        </span>
                                                    )}
                                                    <p id="reminder-time-desc" className="tooltip-calender-event">
                                                        Select when to receive the reminder.
                                                    </p>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                    <button type="submit" disabled={isSubmitLoading} className="submit-client submit-client-cal">
                                        {isSubmitLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <ButtonSkeleton width="100px" height="18px" />
                                            </div>
                                        ) : (
                                            "Create Event"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
        </Modal>
    );
};

export default CreateEventModal;